import { useLineItemsListSections } from "@/pages/(app)/returns/refund/hooks/use-line-items-list-sections";
import useLineItemsQuantitiesSummary from "@/pages/(app)/returns/refund/hooks/use-line-items-quantities-summary";
import {
  useNotReturned,
  useSetNotReturned,
} from "@/pages/(app)/returns/refund/hooks/use-line-items-state-context";
import { useRefundLineItem } from "@/pages/(app)/returns/refund/hooks/use-refund-line-items-context";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import NumberFlow from "@number-flow/react";
import { ArticleItem } from "../article-item/article-item";

export default function ArticlesNotReturned() {
  const t = useTranslation();
  const { itemsNotReturned } = useLineItemsListSections();

  const summary = useLineItemsQuantitiesSummary({ state: "not-returned" });

  if (!itemsNotReturned.length) {
    return null;
  }

  return (
    <AccordionItem value="notReturned">
      <AccordionTrigger>
        <span>
          {t("returnDetail.confirmModal.notReturned")}
          <Badge variant="secondary" className="ml-2">
            <NumberFlow value={summary.selected} suffix={` / ${summary.max}`} />
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="border-border border rounded-md flex-col gap-4 divide-y overflow-hidden">
          {itemsNotReturned.map((lineItem: ReturnRequestLineItemDto) => (
            <ArticleItemNotReturned
              key={lineItem.line_item_id}
              lineItem={lineItem}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

const ArticleItemNotReturned = ({
  lineItem,
}: {
  lineItem: ReturnRequestLineItemDto;
}) => {
  const { item, updateResolution, updateVariant } = useRefundLineItem(lineItem);
  const quantityValue = useNotReturned(lineItem.line_item_id);
  const updateQuantity = useSetNotReturned(lineItem.line_item_id);

  /**
  When an article is not returned, it has no resolution.
  When the merchant adds an article to the request, we need to add a resolution to it.
  So if the quantity is 0, remove the resolution, otherwise add it, default to refund
  */
  const _updateQuantity = (quantity: number) => {
    if (quantity === 0) updateResolution(null);
    else if (!item.resolution) updateResolution("refund");
    updateQuantity(quantity);
  };

  return (
    <ArticleItem
      lineItem={item}
      quantity={quantityValue.selectedQuantity}
      maxQuantity={quantityValue.maxQuantity}
      onChangeQuantity={_updateQuantity}
      onChangeVariant={updateVariant}
      onChangeResolution={updateResolution}
      hideStatus
      hideResolution={quantityValue.selectedQuantity === 0}
    />
  );
};
